import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import SaveIcon from '@mui/icons-material/Save';
import { PagePathname } from '../../../../routes/paths';
import useVertragsvorlage, { VertragsvorlageStart } from '../../hooks/useVertragsvorlage';
import useVorlagensperre, { Vorlagensperre } from '../../hooks/useVorlagensperre';
import VorlagenZuordnung from '../../components/VorlagenZuordnung';
import useVorlagenZuordnungOptionen from '../../hooks/useVorlagenZuordnungOptionen';
import useVertragsartenNeuladbar from '../../hooks/useVertragsartenNeuladbar';
import { NUR_LESEN_STATUS, ZURUECK_ZUR_UEBERSICHT } from '../../services/vorlagensperreTexte';
import usePdfProbe from '../../hooks/usePdfProbe';
import { useVertragEditor } from '../editor/useVertragEditor';
import { APP_KOPF_ABSTAND_PX } from '../editor/VertragEditor';
import { inhaltMindestbreitePx, leistenBedarfPx } from '../layout/leistenplatz';
import { A4_BREITE_MM, mmZuPx } from '../layout/seitengeometrie';
import { gemerkteLeistenposition } from '../layout/useLeistenposition';
import { Vertragsdokument } from '../model/types';
import { Auswahl } from '../model/positionen';
import { pruefeVorlage, Vorlagenhinweis } from '../model/vorlagenpruefung';
import { Beispielauswahl, Beispielfall, STANDARD_BEISPIELFALL } from '../model/beispielfaelle';
import HandbuchArbeitsflaeche from '../../../handbuch/components/HandbuchArbeitsflaeche';
import { LEISTE_BREITE_PX } from './Platzhalterleiste';
import VertragArbeitsbereich from './VertragArbeitsbereich';
import Verlassensschutz from './Verlassensschutz';
import VertragLeistenRahmen, { VertragTitelzeile, useVertragLeisten } from './VertragLeistenRahmen';
import VorlagenZuordnungHinweis from '../../components/VorlagenZuordnungHinweis';
import VorlagenSperrhinweis from './VorlagenSperrhinweis';
import { SpeicherkonfliktDialog, ZweitesFensterDialog } from './VorlagenSperrDialoge';
import { HinweiseSpeichernDialog, Vorlagenhinweise, VorlagenhinweiseLeiste } from './Vorlagenhinweise';
import useKastenAusserSicht from '../layout/useKastenAusserSicht';

export interface VertragsvorlageAnfang {
  anfang: Vertragsdokument;
  start: VertragsvorlageStart;
  unbekanntePlatzhalter: string[];
}

interface Props extends VertragsvorlageAnfang {
  onAngelegt: (id: number) => void;
  /** RITA-1541: lädt die Sitzung mit dem aktuellen gespeicherten Stand neu (Entwurf verwerfen). */
  onNeuLaden: () => void;
}

const RUECKSPRUNG = `${PagePathname.briefvorlagen}?bereich=vertragsvorlagen`;
// RITA-1585: Platz für die Handbuch-Spalte – das ganze A4-Blatt samt Seitenrahmen bleibt neben ihr; die seitliche
// Platzhalterleiste braucht darüber hinaus ihren Rahmenbedarf (sonst steht sie oben).
const BLATT_BREITE_PX = mmZuPx(A4_BREITE_MM);
const INHALT_MINDESTBREITE_PX = inhaltMindestbreitePx(BLATT_BREITE_PX);
const LEISTEN_BEDARF_PX = leistenBedarfPx(BLATT_BREITE_PX, LEISTE_BREITE_PX);
// Wer die Platzhalterleiste bewusst oben stehen hat, gewinnt durch ein schmales Menü nichts an der Seite; dann zählt
// nur der Platz für die Handbuch-Spalte. Die Wahl liegt im Speicher und wird im Moment der Entscheidung gelesen.
const leistenBedarfJeNachWahl = () => (gemerkteLeistenposition() === 'oben' ? null : LEISTEN_BEDARF_PX);

function VertragsvorlageInhalt({ anfang, start, unbekanntePlatzhalter, onAngelegt, onNeuLaden }: Props) {
  const formularRef = useRef<HTMLDivElement>(null);
  const leisten = useVertragLeisten();
  const editor = useVertragEditor(anfang);
  const navigate = useNavigate();
  const location = useLocation();
  const freigabeRef = useRef<() => void>(() => undefined);
  // RITA-1541: Sperre und Speichern kennen sich nur über diese Referenz (beide Hooks brauchen Werte des anderen).
  const sperreRef = useRef<Vorlagensperre | null>(null);
  const vorlage = useVertragsvorlage({
    start,
    editor,
    onAngelegt,
    freigabeRef,
    fensterkennung: () => sperreRef.current?.token,
    vorSpeichern: () => sperreRef.current?.vorSpeichern() ?? true,
    onGespeichert: () => sperreRef.current?.nachSpeichern(),
  });
  const sperre = useVorlagensperre({
    vorlagenId: vorlage.vorlagenId,
    beimOeffnenAnfordern: start.id !== null,
    onNeuLaden,
  });
  sperreRef.current = sperre;
  // Als Eingabe zählen Text, Formatierung, Platzhalter, Name, Beschreibung und Zuordnung (Punkt E);
  // Offenlassen, Scrollen oder Bereichswechsel nicht.
  const eingabespur = [
    vorlage.dokumentText,
    vorlage.name,
    vorlage.beschreibung,
    JSON.stringify(vorlage.zuordnung),
  ].join('\u0000');
  const letzteEingabespur = useRef(eingabespur);
  const { eingabe } = sperre;
  useEffect(() => {
    if (eingabespur === letzteEingabespur.current) return;
    letzteEingabespur.current = eingabespur;
    eingabe();
  }, [eingabespur, eingabe]);
  const zuordnungsoptionen = useVorlagenZuordnungOptionen();
  const vertragsarten = useVertragsartenNeuladbar();
  // RITA-1542: „PDF-Probe" holt die Server-PDF des aktuellen Stands (auch ungespeichert) mit Beispielwerten.
  const pdfProbe = usePdfProbe();
  // RITA-1597: Beispielfall der Ansicht „Beispielwerte“ (beim Öffnen immer „Berater“, wird nicht gespeichert und
  // zählt nicht als Eingabe); {{FIRMA}} zeigt die Marke aus der Zuordnung: den Titel aus der Firmenliste, solange die
  // Liste noch lädt oder die Firma dort fehlt den gespeicherten Markennamen der Vorlage, ohne gewählte Marke den
  // Fallback. Seitenansicht und PDF-Probe bekommen dieselbe Auswahl.
  const [beispielfall, setBeispielfall] = useState<Beispielfall>(STANDARD_BEISPIELFALL);
  const gespeicherteMarke =
    start.assignment && String(start.assignment.branch_office_id) === vorlage.zuordnung.firma
      ? start.assignment.branch_office_name || null
      : null;
  const marke =
    zuordnungsoptionen.firmen.find((firma) => String(firma.id) === vorlage.zuordnung.firma)?.title ?? gespeicherteMarke;
  const beispiel = useMemo<Beispielauswahl>(() => ({ fall: beispielfall, marke }), [beispielfall, marke]);
  // RITA-1599: Hinweise zu typischen Fehlern, live aus dem Dokument und Art/„Für“ der Zuordnung; beim Speichern fragt
  // die Seite nach, solange welche da sind (gesperrt wird nichts).
  const { dokument } = editor.zustand;
  const { art, fuer } = vorlage.zuordnung;
  const hinweise = useMemo(
    () => pruefeVorlage(dokument, { art, fuer }, unbekanntePlatzhalter),
    [dokument, art, fuer, unbekanntePlatzhalter],
  );
  // Entscheidung Julia 03.11.2026: Ist der gelbe Kasten aus dem Bild gescrollt, steht im festen Titel eine orange
  // Leiste mit der Anzahl; „Anzeigen“ holt den Kasten unter die Titelzeile zurück.
  const hinweisKastenRef = useRef<HTMLDivElement>(null);
  // Hinter App-Kopf und klebender Titelzeile (mit Leiste gemessen) ist nichts sichtbar: dieser Streifen zählt nicht.
  const kastenAusserSicht = useKastenAusserSicht(
    hinweisKastenRef,
    hinweise.length > 0,
    leisten?.wertekastenOben ?? APP_KOPF_ABSTAND_PX,
  );
  const [hinweisFrage, setHinweisFrage] = useState<Vorlagenhinweis[] | null>(null);
  const [ziel, setZiel] = useState<string | null>(null);
  const verlassen = (adresse: string) => {
    if (vorlage.speichert) return;
    if (vorlage.geaendert) setZiel(adresse);
    else navigate(adresse);
  };
  const titel =
    vorlage.vorlagenId === null ? 'Neue Vertragsvorlage' : vorlage.name.trim() || 'Vertragsvorlage bearbeiten';
  // PDF-Metadaten (Tab-Titel der PDF): der eingegebene Vorlagenname, auch vor dem ersten Speichern (KI-Review !773).
  const pdfTitel = vorlage.name.trim() || 'Vertragsvorlage';
  let speicherstand = vorlage.vorlagenId !== null ? 'Gespeichert' : '';
  let speicherfarbe = 'success.main';
  if (vorlage.geaendert) {
    speicherstand = 'Nicht gespeicherte Änderungen';
    speicherfarbe = 'error.main';
  }
  if (sperre.nurLesen) {
    speicherstand = NUR_LESEN_STATUS;
    speicherfarbe = 'text.secondary';
  }
  // Nur lesen (fremde Sperre, anderes Fenster, frei) sperrt alles wie das Speichern. Während der kurzen
  // Prüfung beim Öffnen bleibt die Seite bedienbar; vor dem Speichern wird die Sperre ohnehin geprüft.
  const gesperrt = vorlage.speichert || sperre.nurLesen;
  // Auf schmalen Fenstern scrollt die begrenzte Titelzeile selbst. Eine neue Fehlermeldung
  // muss auch dort sichtbar werden, ohne die Eingaben oder den Editor zurückzusetzen.
  useEffect(() => {
    if (vorlage.hinweisOben) {
      formularRef.current
        ?.querySelector<HTMLElement>('[data-testid="vertragsvorlage-speicherfehler"]')
        ?.scrollIntoView?.({ block: 'nearest' });
    }
  }, [vorlage.hinweisOben]);

  const speichern = async () => {
    await vorlage.speichern();
    const feld = formularRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
    if (!feld) return;
    feld.focus({ preventScroll: true });
    feld.scrollIntoView?.({ block: 'center' });
    // Formularfelder liegen vor den Editorleisten. Nur die feste Titelzeile kann sie verdecken.
    const oben = Math.max(leisten?.appAbstand ?? 0, leisten?.kopfRef.current?.getBoundingClientRect().bottom ?? 0) + 4;
    const rect = feld.getBoundingClientRect();
    if (rect.height > 0 && rect.top < oben) window.scrollBy({ top: rect.top - oben });
  };

  /**
   * RITA-1599: Mit Hinweisen erst nachfragen – aber nur, wenn das Speichern die Eingaben annehmen würde; sonst zeigt
   * Speichern wie bisher zuerst den Fehler am Feld.
   */
  const speichernKlick = () => {
    if (hinweise.length > 0 && vorlage.eingabenGueltig()) {
      setHinweisFrage(hinweise);
      return;
    }
    speichern().catch(() => undefined);
  };

  /** RITA-1599: „Stelle zeigen“ – die Fundstelle markieren und in den Blick holen (wie ein Klick an diese Stelle). */
  const stelleZeigen = (stelle: Auswahl) =>
    editor.befehle.bereichWechseln(stelle.anker.bereich, editor.zustand.aktiveSeite, stelle);

  /** „Anzeigen“ in der festen Leiste: den gelben Kasten direkt unter die klebende Titelzeile holen und fokussieren. */
  const hinweiseAnzeigen = () => {
    const kasten = hinweisKastenRef.current;
    if (!kasten) return;
    const kopfUnten = leisten?.kopfRef.current?.getBoundingClientRect().bottom ?? 0;
    window.scrollBy(0, kasten.getBoundingClientRect().top - kopfUnten - 8);
    kasten.focus({ preventScroll: true });
  };

  // RITA-1585: Der Knopf „Handbuch“ steht rechts neben der Pfadzeile, nicht darin (die Pfadzeile bleibt wie bisher).
  const pfadzeile = (
    <Box
      component="nav"
      aria-label="Pfadzeile"
      sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mb: 1, gap: 0.5 }}
    >
      <Button
        size="small"
        disabled={vorlage.speichert}
        onClick={() => verlassen(PagePathname.einstellung)}
        sx={{ color: 'text.secondary', minWidth: 0 }}
      >
        Einstellungen
      </Button>
      <Typography color="text.secondary">›</Typography>
      <Button
        size="small"
        disabled={vorlage.speichert}
        onClick={() => verlassen(RUECKSPRUNG)}
        sx={{ color: 'text.secondary', minWidth: 0 }}
      >
        Vorlagen
      </Button>
      <Typography color="text.secondary">›</Typography>
      <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
        {titel}
      </Typography>
    </Box>
  );

  return (
    <HandbuchArbeitsflaeche
      seite="vorlagenEditor"
      pfadzeile={pfadzeile}
      obenAbstandPx={leisten?.appAbstand ?? APP_KOPF_ABSTAND_PX}
      mindestInhaltBreitePx={INHALT_MINDESTBREITE_PX}
      leistenBedarfPx={leistenBedarfJeNachWahl}
      rahmenRef={formularRef}
    >
      <Verlassensschutz key={location.key} geaendert={vorlage.geaendert} freigabeRef={freigabeRef} />
      <VertragTitelzeile
        titel={titel}
        status={
          <Typography
            role="status"
            data-testid="vertragsvorlage-speicherstand"
            variant="caption"
            sx={{ color: speicherfarbe }}
          >
            {speicherstand}
          </Typography>
        }
        hinweis={
          hinweise.length > 0 && kastenAusserSicht ? (
            <VorlagenhinweiseLeiste anzahl={hinweise.length} onAnzeigen={hinweiseAnzeigen} />
          ) : null
        }
        fehler={
          <>
            {/* RITA-1541: Der Sperrhinweis erscheint unabhängig von Speicher- oder PDF-Fehlern. */}
            <VorlagenSperrhinweis sperre={sperre} />
            {vorlage.hinweisOben ? (
              <Alert
                severity="error"
                data-testid="vertragsvorlage-speicherfehler"
                sx={{ mt: 1, overflowWrap: 'anywhere' }}
              >
                {vorlage.hinweisOben}
              </Alert>
            ) : null}
            {pdfProbe.fehler ? (
              <Alert
                severity="error"
                data-testid="vertragsvorlage-pdf-fehler"
                sx={{ mt: 1 }}
                onClose={pdfProbe.fehlerSchliessen}
              >
                {pdfProbe.fehler}
              </Alert>
            ) : null}
          </>
        }
        aktionen={
          <>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              disabled={vorlage.speichert}
              onClick={() => verlassen(RUECKSPRUNG)}
            >
              {sperre.nurLesen ? ZURUECK_ZUR_UEBERSICHT : 'Abbrechen'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfOutlinedIcon />}
              disabled={vorlage.speichert || pdfProbe.laeuft}
              onClick={() => pdfProbe.oeffnen(editor.zustand, pdfTitel, beispiel)}
            >
              {pdfProbe.laeuft ? 'PDF wird erstellt …' : 'PDF-Probe'}
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={gesperrt || zuordnungsoptionen.laedt || zuordnungsoptionen.fehler}
              onClick={speichernKlick}
            >
              {vorlage.speichert ? 'Wird gespeichert …' : 'Speichern'}
            </Button>
          </>
        }
      />
      <Box
        data-testid="vertragsvorlage-stammdaten"
        sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.4fr)', gap: 2, mb: 2 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="label"
            htmlFor="vertragsvorlage-name"
            sx={{
              display: { xs: 'block', sm: 'none' },
              fontSize: 12,
              lineHeight: 1.4,
              minHeight: 34,
              mb: 0.5,
              overflowWrap: 'anywhere',
            }}
          >
            Vorlagenname *
          </Typography>
          <TextField
            id="vertragsvorlage-name"
            fullWidth
            required
            size="small"
            label="Vorlagenname"
            value={vorlage.name}
            disabled={gesperrt}
            onChange={(event) => vorlage.setName(event.target.value)}
            error={!!vorlage.nameFehler}
            helperText={vorlage.nameFehler}
            inputProps={{ maxLength: 120, 'aria-label': 'Vorlagenname' }}
            InputLabelProps={{ sx: { display: { xs: 'none', sm: 'block' } } }}
            sx={{ '& legend': { display: { xs: 'none', sm: 'block' } } }}
          />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="label"
            htmlFor="vertragsvorlage-beschreibung"
            sx={{
              display: { xs: 'block', sm: 'none' },
              fontSize: 12,
              lineHeight: 1.4,
              minHeight: 34,
              mb: 0.5,
              overflowWrap: 'anywhere',
            }}
          >
            Interne Beschreibung (optional)
          </Typography>
          <TextField
            id="vertragsvorlage-beschreibung"
            fullWidth
            size="small"
            label="Interne Beschreibung (optional)"
            value={vorlage.beschreibung}
            disabled={gesperrt}
            onChange={(event) => vorlage.setBeschreibung(event.target.value)}
            error={!!vorlage.beschreibungFehler}
            helperText={vorlage.beschreibungFehler}
            inputProps={{ maxLength: 250, 'aria-label': 'Interne Beschreibung (optional)' }}
            InputLabelProps={{ sx: { display: { xs: 'none', sm: 'block' } } }}
            sx={{ '& legend': { display: { xs: 'none', sm: 'block' } } }}
          />
        </Box>
      </Box>
      <VorlagenZuordnung
        wert={vorlage.zuordnung}
        onChange={vorlage.setZuordnung}
        fehler={vorlage.zuordnungsfehler}
        gesperrt={gesperrt}
        optionen={zuordnungsoptionen}
        hinweis={
          <VorlagenZuordnungHinweis
            wert={vorlage.zuordnung}
            vorlagenId={vorlage.vorlagenId}
            kopie={start.kopie && vorlage.vorlagenId === null}
          />
        }
      />
      {/* RITA-1599: gelber Kasten direkt unter der Zuordnung, über dem Arbeitsbereich; klebt nicht – außer Sicht
          vertritt ihn die orange Leiste im Titel. */}
      <Vorlagenhinweise
        hinweise={hinweise}
        gesperrt={gesperrt}
        kastenRef={hinweisKastenRef}
        onStelleZeigen={stelleZeigen}
      />
      {unbekanntePlatzhalter.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Dieser Vertrag enthält Platzhalter, die es nicht mehr gibt:{' '}
          {unbekanntePlatzhalter.map((name) => `{{${name}}}`).join(', ')}. Sie werden als normaler Text behandelt.
        </Alert>
      )}
      {vertragsarten.stand.fehler && editor.zustand.dokument.kopf.logo.vertragsartId !== null && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Das Logo der gespeicherten Vertragsart kann gerade nicht geladen werden. Die gespeicherte Auswahl bleibt
          erhalten.
        </Alert>
      )}
      <VertragArbeitsbereich
        editor={editor}
        vertragsarten={vertragsarten.stand}
        gesperrt={gesperrt}
        onVertragsartenErneutLaden={vertragsarten.erneutLaden}
        beispiel={beispiel}
        onBeispielfallChange={setBeispielfall}
      />
      <ZweitesFensterDialog
        halter={sperre.status === 'zweitesFensterFrage' ? sperre.halter : null}
        onNurLesen={sperre.nurLesenWaehlen}
        onWeiter={sperre.hierWeiterbearbeiten}
      />
      <SpeicherkonfliktDialog
        gespeichertUm={vorlage.konflikt}
        speichert={vorlage.speichert}
        // Gleiche Person, gleiches Fenster: die Sperre bleibt beim Austausch der Sitzung bestehen.
        onNeuLaden={sperre.aktuellenStandLaden}
        onTrotzdemSpeichern={() => vorlage.speichern({ ueberschreiben: true })}
      />
      <HinweiseSpeichernDialog
        hinweise={hinweisFrage}
        onZurueck={() => setHinweisFrage(null)}
        onTrotzdemSpeichern={() => {
          setHinweisFrage(null);
          speichern().catch(() => undefined);
        }}
      />
      <Dialog open={ziel !== null} onClose={() => setZiel(null)} aria-labelledby="vertragsvorlage-abbrechen-titel">
        <DialogTitle id="vertragsvorlage-abbrechen-titel">Bearbeitung abbrechen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchtest du die Bearbeitung abbrechen? Alle nicht gespeicherten Eingaben gehen verloren.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setZiel(null)}>Weiterbearbeiten</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (ziel !== null) {
                freigabeRef.current();
                navigate(ziel);
              }
            }}
          >
            Verwerfen und zurück
          </Button>
        </DialogActions>
      </Dialog>
    </HandbuchArbeitsflaeche>
  );
}

export default function VertragsvorlageFormular({
  anfang,
  start,
  unbekanntePlatzhalter,
  onAngelegt,
  onNeuLaden,
}: Props) {
  return (
    <VertragLeistenRahmen>
      <VertragsvorlageInhalt
        anfang={anfang}
        start={start}
        unbekanntePlatzhalter={unbekanntePlatzhalter}
        onAngelegt={onAngelegt}
        onNeuLaden={onNeuLaden}
      />
    </VertragLeistenRahmen>
  );
}
